import WarningIcon from "@material-ui/icons/Warning";
import { matchSorter } from "match-sorter";
import dayjs from "dayjs";
import { z } from "zod";
import { isBoolean } from "lodash";
import { Alert } from "@mui/material";

import i18n, { Trans } from "../utils/i18n";
import { validateEmailRegEx, validatePhoneNumber } from "../Shared/FormField";
import { addressConfigB2b } from "./components/address_b2b";
import { getProductsQueryOptions } from "../hooks/queries/useProductsQuery";
import HelpModal from "../Shared/HelpModal";
import { getCookieValue } from "../utils/getCookieValue";

const WARRANTY_TYPES = {
    "STANDARD_LEGAL_WARRANTY_POP_DATE": "Standard Legal Warranty (PoP Date)",
};

const validProblem = (values, orderEntryState) => {
    const serialNumber = orderEntryState && orderEntryState.serialNumber;

    const currentProblem = serialNumber && serialNumber.product?.problems.find(item => parseInt(values.problem_1) === item.id);
    const currentProblem2 = currentProblem && currentProblem.problems?.find(item => parseInt(values.problem_2) === item.id);

    return values.problem_1 && values.problem_2 && !currentProblem2?.education_text;
};

const validateWarrantyDependantFields = (val, ctx) => {
    if ((val.warranty === false || (val.warranty === true && val.warrantyType === WARRANTY_TYPES.STANDARD_LEGAL_WARRANTY_POP_DATE)) && !val.pop_date) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t("dateofpurchase.requiredIfNoWarranty"),
            path: ["pop_date"],
        });

        // Abort early
        return z.NEVER;
    }

    if ((val.warranty === false || (val.warranty === true && val.warrantyType === WARRANTY_TYPES.STANDARD_LEGAL_WARRANTY_POP_DATE)) && (!val.files || val.files?.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t("files.required"),
            path: ["files"],
        });

        // Abort early
        return z.NEVER;
    }
};

const validateUniqueSerialNumber = (val, ctx) => {
    if (
        val.serialnumber &&
        val.products?.some(
            (product) => product.serialnumber === val.serialnumber
        )
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t("serialnumber.alreadyAdded"),
            path: ["serialnumber"],
        });

        // Abort early
        return z.NEVER;
    }

    return true;
};

const ConfigurationProductSchema = z.object({
    id: z.number(),
    serialnumber: z.string().min(1),
    requesttype: z.string().min(1),
    warranty: z.boolean(),
});

const ProductSelectionGroupSchema = z.object({
    requesttype: z.string().min(1),
    productcategory: z.string().min(1),
    configurationProduct: z.object({ id: z.number(), recall_text: z.string().nullable() }).required().refine(product => !product.recall_text),
    orderattributes: z.object({
        send_accessories: z.string().min(1).refine(val => val === "yes" || val === "no"),
        accesories_description: z.string().optional(),
    }),
    // "orderattributes.send_accessories": z.string().min(1).refine(val => val === "yes" || val === "no"),
    // "orderattributes.accesories_description": z.string().optional(),
});

const WarrantyCheckGroupSchema = z.object({
    serialnumber: z.string().min(1),
    warranty: z.boolean(),
    pop_date: z.string().nullable(),
    files: z.array(z.any()).nullable(),
    problem_description: z.string().nullable(),
});

const WarrantyCheckGroupSchemaSuperRefined = z
    .object({
        serialnumber: z.string().min(1),
        warranty: z.boolean(),
        pop_date: z.string().min(1).refine(val => {
            if (val === "") {
                return false;
            }
            const date = dayjs(val, "DD.MM.YYYY", true);
            return date.isValid();
        }),
        files: z.array(z.any()).nullable(),
        products: z.array(ConfigurationProductSchema).optional(),
        warrantyType: z.string().nullable().optional(),
    })
    .superRefine(validateUniqueSerialNumber)
    .superRefine(validateWarrantyDependantFields);

const WorkflowQuestionsSchema = z.object({
    problem_1: z.string().min(1),
    problem_2: z.string().min(1),
    workflow_questions_valid: z.boolean().refine((val) => !!val, {
        message: i18n.t("workflowQuestions.pleaseAnswerAllQuestions"),
    }),
    workflow_questions: z.array(z.any()).nullable().optional(),
    problem_description: z.string().nullable(),
    customer_reference: z.string().nullable(),
});

export const OrderEntryBissellB2BSchema = z
    .object({
        products: z.array(ConfigurationProductSchema).min(1),
    });

export const orderEntryConfigBissellB2B = {
    schema: OrderEntryBissellB2BSchema,
    sizes: {
        forth: 3,
        third: 4,
        half: 6,
        full: 12,
    },
    enableStepper: true,
    heading: <Trans i18nKey="orderEntry.basicData">Basic data</Trans>,
    prepareSubmitData: (values) => {
        return {
            ...values,
            products: values.products
                // Override the type to repair, so that the products can be handled correctly in the backend (e. g. price calculation)
                .map((product) => ({
                    ...product,
                    type: "repair",
                })),
        };
    },
    // Form State
    defaultValues: {
        private_or_company: "company",
        requests: [],
        products: [],
        configurationProduct: null,
    },
    // Global State
    initialState: {},
    groups: [
        {
            schema: ProductSelectionGroupSchema,
            heading: (
                <Trans i18nKey="orderEntry.productSelection">
                    Product selection
                </Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.productSelection.description"></Trans>
            ),
            id: "product_selection",
            fields: [
                ({ values, form }) => ({
                    size: "full",
                    component: "devicewidgetb2b",
                    // disablePortal: false,
                    // portalContainerId: "sidebar",
                    className: "!mt-6",
                    // portalClassName: "!mt-10 min-w-[400px]",
                    configurationProduct: values.configurationProduct
                        ? {
                              ...values.configurationProduct,
                              serialnumber:
                                  values.configurationProduct?.serialnumber ||
                                  values.serialnumber,
                              requesttype:
                                  values.configurationProduct?.requesttype ||
                                  values.requesttype,
                              warranty:
                                  values.configurationProduct?.warranty ||
                                  values.warranty,
                          }
                        : null,
                    products: values.products,
                    onDelete: (product) => {
                        form.change(
                            "products",
                            values.products.filter((p) => p.id !== product.id)
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            !!values?.configurationProduct ||
                            values?.products?.length > 0
                        );
                    },
                }),
                {
                    size: "full",
                    component: "requesttypeselect",
                    doNotResetForm: true, // Otherwise it would reset e. g. the "products" array
                },
                ({ form, dispatch, values }) => ({
                    size: "full",
                    name: "productcategory",
                    component: "productcategoryselector",
                    columns: 4,
                    onChange: () => {
                        // Reset relevant form values
                        [
                            ...Object.keys(
                                ProductSelectionGroupSchema.pick({
                                    configurationProduct: true,
                                }).shape
                            ),
                            ...Object.keys(WarrantyCheckGroupSchema.shape),
                            ...Object.keys(WorkflowQuestionsSchema.shape),
                        ].forEach((key) => form.change(key, null));

                        // Reset orderEntryState
                        dispatch({
                            type: "setValue",
                            key: "serialNumber",
                            value: {},
                        });
                        dispatch({
                            type: "setValue",
                            key: "files",
                            value: [],
                        });
                    },
                    queryOptions: {
                        queryKey: ["productcategories"],
                        queryParameters: {
                            filter: {
                                is_orderable: true,
                            },
                        },
                        onSuccess: (categories = []) => {
                            if (categories[0] && !values?.productcategory) {
                                form.change(
                                    "productcategory",
                                    categories[0].id.toString()
                                );
                            }
                        },
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                        }).safeParse(values).success;
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: () => (
                        <h3 className="m-0">
                            <Trans i18nKey="orderEntry.productSelect.heading">
                                Please select your product
                            </Trans>
                        </h3>
                    ),
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                        }).safeParse(values).success;
                    },
                },
                ({ form, values, dispatch }) => ({
                    size: "full",
                    component: "autocomplete",
                    name: "configurationProduct",
                    onChange: () => {
                        // Reset relevant form values
                        [
                            ...Object.keys(WarrantyCheckGroupSchema.shape),
                            ...Object.keys(WorkflowQuestionsSchema.shape),
                        ].forEach((key) => form.change(key, null));

                        // Reset orderEntryState
                        dispatch({
                            type: "setValue",
                            key: "serialNumber",
                            value: {},
                        });
                        dispatch({
                            type: "setValue",
                            key: "files",
                            value: [],
                        });
                    },
                    queryOptions: getProductsQueryOptions({
                        queryKey: ["products"],
                        queryParameters: {
                            filter: {
                                type: "unit",
                                ...(values?.productcategory !== "all" && {
                                    "productcategory.id":
                                        values?.productcategory,
                                }),
                            },
                            // We need to include the productcategory, because when we edit a configured product, we need to know the category
                            // and set it as selected in the form.
                            include: ["productcategory"].join(","),
                        },
                        enabled: !!values?.productcategory,
                    }),
                    // Allow to filter by description and number
                    filterOptions: (options, { inputValue }) =>
                        matchSorter(options, inputValue, {
                            keys: ["description", "number"],
                        }),
                    // Render the selected option
                    getOptionLabel: (option) => {
                        if (!option) return "";
                        return `${option?.description ?? ""} (${
                            option?.number ?? ""
                        })`;
                    },
                    // Render the option within the combobox
                    renderOption: (props, option) => {
                        if (!option)
                            return (
                                <div {...props} key={option?.id}>
                                    ""
                                </div>
                            );
                        return (
                            <div {...props} key={option?.id}>
                                {option?.description ?? ""} (
                                {option?.number ?? ""})
                            </div>
                        );
                    },
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                        }).safeParse(values).success;
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: () => (
                        <div className="flex items-center justify-center">
                            <HelpModal
                                title={
                                    <Trans i18nKey="productNumber.cannotFind">
                                        Can't find the product number?
                                    </Trans>
                                }
                                className="md:max-w-4xl"
                            >
                                <>
                                    <h2 className="font-semibold text-lg leading-6 m-0">
                                        <Trans i18nKey="productNumber.whereToFindTitle">
                                            Model Number
                                        </Trans>
                                    </h2>
                                    <p>
                                        <Trans i18nKey="productNumber.whereToFindText1">
                                            Not sure where to look?
                                        </Trans>
                                    </p>
                                    <p>
                                        <Trans i18nKey="productNumber.whereToFindText2">
                                            <strong>On machines</strong> it’s on a white label like this. It’s usually on the lower back or bottom of the machine (for Lift-Off® models, remove the Lift-Off pod to find label) or behind the cleaning tanks.
                                        </Trans>
                                    </p>
                                    <p>
                                        <img
                                            src="images/bissell/FINAL_LABEL.png"
                                            alt=""
                                            className="w-full"
                                        />
                                    </p>
                                </>
                            </HelpModal>
                        </div>
                    ),
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                        }).safeParse(values).success;
                    },
                },
                {
                    size: "full",
                    component: "callback",
                    callback: () => (
                        <h3 className="m-0">
                            <Trans i18nKey="orderEntry.productSelect.accessories">
                                Will you send accessories with the product?
                            </Trans>
                        </h3>
                    ),
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                            configurationProduct: true,
                        }).safeParse(values).success;
                    },
                },
                {
                    "name": "orderattributes.send_accessories",
                    "titel": <Trans i18nKey="common.yes">Yes</Trans>,
                    "size": "half",
                    "type": "radiobig",
                    "value": "yes",
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                            configurationProduct: true,
                        }).safeParse(values).success;
                    },
                },
                {
                    "name": "orderattributes.send_accessories",
                    "titel": <Trans i18nKey="common.no">No</Trans>,
                    "size": "half",
                    "type": "radiobig",
                    "value": "no",
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                            configurationProduct: true,
                        }).safeParse(values).success;
                    },
                },
                {
                    "size": "full",
                    "name": "orderattributes.accesories_description",
                    "label": <Trans i18nKey="orderEntry.accessoriesDescription">What accessories accessory will you send with the product?</Trans>,
                    "multiline": true,
                    "validatetype": "max200chars",
                    "conditionFunction": (values, orderEntryState) => {
                        return ProductSelectionGroupSchema.pick({
                            requesttype: true,
                            productcategory: true,
                            configurationProduct: true,
                        }).safeParse(values).success && values.orderattributes?.send_accessories === "yes";
                    }
                },
                ({ values }) => ({
                    size: "full",
                    component: "callback",
                    callback: () => (
                        <Alert severity="error">
                            <div dangerouslySetInnerHTML={{ __html: values?.configurationProduct?.recall_text }} />
                        </Alert>
                    ),
                    conditionFunction: (
                        values,
                        orderEntryState,
                        currentCountry
                    ) => {
                        return !!values?.configurationProduct?.recall_text;
                    },
                }),
            ],
            noNextButton: false,
        },
        {
            schema: WarrantyCheckGroupSchemaSuperRefined,
            heading: (
                <Trans i18nKey="orderEntry.warrantyCheck">Warranty Check</Trans>
            ),
            id: "warrantyCheck",
            fields: [
                ({ values, form }) => ({
                    size: "full",
                    component: "devicewidgetb2b",
                    className: "!mt-6",
                    configurationProduct: values.configurationProduct
                        ? {
                              ...values.configurationProduct,
                              serialnumber:
                                  values.configurationProduct?.serialnumber ||
                                  values.serialnumber,
                              requesttype:
                                  values.configurationProduct?.requesttype ||
                                  values.requesttype,
                              warranty:
                                  values.configurationProduct?.warranty ||
                                  values.warranty,
                          }
                        : null,
                    products: values.products,
                    onDelete: (product) => {
                        form.change(
                            "products",
                            values.products.filter((p) => p.id !== product.id)
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            !!values?.configurationProduct ||
                            values?.products?.length > 0
                        );
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3>
                                <Trans i18nKey={"orderEntry.enterSerialNumber"}>
                                    Enter the serial number of your product
                                </Trans>
                            </h3>
                        </>
                    ),
                },
                ({ form, dispatch }) => ({
                    component: "serialnumber",
                    size: "full",
                    required: true,
                    uppercase: true,
                    doNotShowConfirmDevice: true,
                    onChangeCallback: () => {
                        // Reset relevant form values
                        [
                            ...Object.keys(
                                WarrantyCheckGroupSchema.omit({
                                    serialnumber: true,
                                }).shape
                            ),
                            ...Object.keys(WorkflowQuestionsSchema.shape),
                        ].forEach((key) => form.change(key, null));

                        // Reset orderEntryState
                        dispatch({
                            type: "setValue",
                            key: "serialNumber",
                            value: {},
                        });
                        dispatch({
                            type: "setValue",
                            key: "files",
                            value: [],
                        });
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3>
                                <Trans
                                    i18nKey={"orderEntry.upload-receipt-only"}
                                >
                                    Upload receipt
                                </Trans>
                            </h3>
                            <p>
                                <Trans
                                    i18nKey={
                                        "orderEntry.upload-receipt-only.description"
                                    }
                                >
                                    Please upload your invoice or receipt. You
                                    can upload JPG, PNG or PDF files up to 20 MB
                                    size.
                                </Trans>
                            </p>
                        </>
                    ),
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            ((values?.warranty === true &&
                                values?.warrantyType === WARRANTY_TYPES.STANDARD_LEGAL_WARRANTY_POP_DATE) ||
                                values?.warranty === false) &&
                            WarrantyCheckGroupSchema.extend({
                                products: z
                                    .array(ConfigurationProductSchema)
                                    .optional(),
                            })
                                .pick({ serialnumber: true, products: true })
                                .superRefine(validateUniqueSerialNumber)
                                .safeParse(values).success
                        );
                    },
                },
                {
                    size: "full",
                    component: "dropzoneform",
                    type: "receipt",
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            ((values?.warranty === true &&
                                values?.warrantyType === WARRANTY_TYPES.STANDARD_LEGAL_WARRANTY_POP_DATE) ||
                                values?.warranty === false) &&
                            WarrantyCheckGroupSchema.extend({
                                products: z
                                    .array(ConfigurationProductSchema)
                                    .optional(),
                            })
                                .pick({ serialnumber: true, products: true })
                                .superRefine(validateUniqueSerialNumber)
                                .safeParse(values).success
                        );
                    },
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 className={"mt-6"}>
                                <Trans i18nKey={"orderEntry.enterPopDate"}>
                                    Enter the date of purchase
                                </Trans>
                            </h3>
                        </>
                    ),
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            ((values?.warranty === true &&
                                values?.warrantyType === WARRANTY_TYPES.STANDARD_LEGAL_WARRANTY_POP_DATE) ||
                                values?.warranty === false) &&
                            WarrantyCheckGroupSchema.extend({
                                products: z
                                    .array(ConfigurationProductSchema)
                                    .optional(),
                            })
                                .pick({ serialnumber: true, products: true })
                                .superRefine(validateUniqueSerialNumber)
                                .safeParse(values).success
                        );
                    },
                },
                ({ values, form, dispatch }) => ({
                    name: "pop_date",
                    label: (
                        <Trans i18nKey="orderEntry.pop_date">
                            Date of purchase
                        </Trans>
                    ),
                    onChangeCallback: async (date) => {
                        try {
                            const response = await fetch(
                                route("order.serialnumber"),
                                {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                        "X-XSRF-TOKEN":
                                            getCookieValue("XSRF-TOKEN"),
                                        "X-Requested-With": "XMLHttpRequest",
                                    },
                                    body: JSON.stringify({
                                        serialnumber: values.serialnumber,
                                        type: values.requesttype,
                                        selectedProductId:
                                            values?.configurationProduct?.id,
                                        pop_date: date,
                                    }),
                                }
                            );
                            const data = await response.json();
                            if (isBoolean(data?.warranty)) {
                                form.change("warranty", data.warranty);
                                // Keep track of the warranty type. We need this for the condition functions below
                                // in order to still show these fields, even if the warranty is true after pop_date was set.
                                form.change("warrantyType", data.warrantyType);
                                form.change("configurationProduct", {
                                    ...values.configurationProduct,
                                    warranty: data.warranty,
                                });
                            }
                        } catch (error) {
                            console.error(error);
                            // silence
                        }
                    },
                    size: "full",
                    type: "datepicker",
                    required: true,
                    allowPast: true,
                    allowToolbar: true,
                    maxDate: dayjs().toISOString(),
                    conditionFunction: (values, orderEntryState) => {
                        return !!values?.serialnumber && isBoolean(values?.warranty);
                    },
                }),
            ],
            conditionFunction: (values, orderEntryState) => {
                return ProductSelectionGroupSchema.safeParse(values).success;
            },
        },
        {
            schema: WorkflowQuestionsSchema,
            heading: (
                <Trans i18nKey="orderEntry.problemDescription">
                    Problem Description
                </Trans>
            ),
            id: "workflowQuestions",
            fields: [
                ({ values, form }) => ({
                    size: "full",
                    component: "devicewidgetb2b",
                    className: "!mt-6",
                    configurationProduct: values.configurationProduct
                        ? {
                              ...values.configurationProduct,
                              serialnumber:
                                  values.configurationProduct?.serialnumber ||
                                  values.serialnumber,
                              requesttype:
                                  values.configurationProduct?.requesttype ||
                                  values.requesttype,
                              warranty:
                                  values.configurationProduct?.warranty ||
                                  values.warranty,
                          }
                        : null,
                    products: values.products,
                    onDelete: (product) => {
                        form.change(
                            "products",
                            values.products.filter((p) => p.id !== product.id)
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            !!values?.configurationProduct ||
                            values?.products?.length > 0
                        );
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 style={{ marginBottom: 0 }}>
                                <Trans i18nKey={"orderEntry.problem.heading"}>
                                    What happend?
                                </Trans>
                            </h3>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "problemselectbig",
                    hideProblemInput: true,
                },
                {
                    size: "full",
                    component: "workflowquestions",
                    conditionFunction: (values, orderEntryState) => {
                        return WorkflowQuestionsSchema.pick({
                            problem_1: true,
                            problem_2: true,
                        }).safeParse(values).success && validProblem(values, orderEntryState);
                    },
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 style={{ marginBottom: 0 }}>
                                <Trans i18nKey={"orderEntry.problemDescription"}>
                                    Problem description
                                </Trans>
                            </h3>
                        </>
                    ),
                    "conditionFunction": (values, orderEntryState) => {
                        return WorkflowQuestionsSchema.pick({
                            problem_1: true,
                            problem_2: true,
                        }).safeParse(values).success && validProblem(values, orderEntryState);
                    }
                },
                {
                    "size": "full",
                    "name": "problem_description",
                    "label": <Trans i18nKey="serialNumber.problemDescription">Problem description</Trans>,
                    "multiline": true,
                    "validatetype": "max200chars",
                    "conditionFunction": (values, orderEntryState) => {
                        return WorkflowQuestionsSchema.pick({
                            problem_1: true,
                            problem_2: true,
                        }).safeParse(values).success && validProblem(values, orderEntryState);
                    }
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                            <h3 style={{ marginBottom: 0 }}>
                                <Trans i18nKey="orderEntry.customerReference">Customer reference</Trans>
                            </h3>
                    ),
                    "conditionFunction": (values, orderEntryState) => {
                        return WorkflowQuestionsSchema.pick({
                            problem_1: true,
                            problem_2: true,
                        }).safeParse(values).success && validProblem(values, orderEntryState);
                    }
                },
                {
                    "name": "customer_reference",
                    "label": <Trans i18nKey="orderEntry.customerReference">Customer reference</Trans>,
                    "size": "full",
                    "conditionFunction": (values, orderEntryState) => {
                        return WorkflowQuestionsSchema.pick({
                            problem_1: true,
                            problem_2: true,
                        }).safeParse(values).success && validProblem(values, orderEntryState);
                    }
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                return (
                    ProductSelectionGroupSchema.safeParse(values).success &&
                    WarrantyCheckGroupSchemaSuperRefined.safeParse(values)
                        .success
                );
            },
            // Confirm device
            onNextButtonClick: ({ form, values, send, dispatch }) => {
                // Add the product
                form.change("products", [
                    ...(values?.products ?? []),
                    {
                        ...values.configurationProduct,

                        // Since we have a multi order and send 1 order request for each product,
                        // we need to add these global fields to the products, so that each "product configuration flow"
                        // can have its own serialnumber, requesttype, etc. Otherwise they would be overridden globally
                        // after a new product is being configured.
                        serialnumber: values.serialnumber,
                        requesttype: values.requesttype,
                        warranty: values.warranty,
                        fileIds: values.files?.map(
                            (file) => file?.serverResponse?.id
                        ),
                        pop_date: values.pop_date,
                        problem_1: values.problem_1,
                        problem_2: values.problem_2,
                        workflow_questions: values.workflow_questions,
                        problem_description: values.problem_description,
                        orderattributes: values.orderattributes,
                        customer_reference: values.customer_reference,
                    },
                ]);

                // Reset relevant form values
                [
                    ...Object.keys(ProductSelectionGroupSchema.shape),
                    ...Object.keys(WarrantyCheckGroupSchema.shape),
                    // ...Object.keys(WorkflowQuestionsSchema.shape),
                ].forEach((key) => form.change(key, null));

                // Reset orderEntryState
                dispatch({
                    type: "setValue",
                    key: "serialNumber",
                    value: {},
                });
                dispatch({
                    type: "setValue",
                    key: "files",
                    value: [],
                });
            },
            nextButtonText: (
                <Trans i18nKey="orderEntry.addDeviceToOrder">
                    Add device to order
                </Trans>
            ),
        },
        {
            heading: "Dummy",
            hideFromStepper: true,
            id: "confirmation",
            fields: [
                ({ values, form }) => ({
                    size: "full",
                    component: "devicewidgetb2b",
                    className: "!mt-6",
                    configurationProduct: values.configurationProduct
                        ? {
                              ...values.configurationProduct,
                              serialnumber:
                                  values.configurationProduct?.serialnumber ||
                                  values.serialnumber,
                              requesttype:
                                  values.configurationProduct?.requesttype ||
                                  values.requesttype,
                              warranty:
                                  values.configurationProduct?.warranty ||
                                  values.warranty,
                          }
                        : null,
                    products: values.products,
                    onDelete: (product) => {
                        form.change(
                            "products",
                            values.products.filter((p) => p.id !== product.id)
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            !!values?.configurationProduct ||
                            values?.products?.length > 0
                        );
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3>
                                <Trans i18nKey={"orderEntry.addNewDeviceStep"}>
                                    Do you want to add another device?
                                </Trans>
                            </h3>
                        </>
                    ),
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                return [
                    WorkflowQuestionsSchema.safeParse(values).success,
                ].every((isValid) => isValid) && validProblem(values, orderEntryState);
            },
            prevButtonText: (
                <Trans i18nKey="orderEntry.addNewDevice">
                    Add another device
                </Trans>
            ),
            onPrevButtonClick: ({ dispatch, form }) => {
                // Go back to step 1
                dispatch({
                    type: "setActiveStep",
                    value: {
                        index: 0,
                        id: "product_selection",
                    },
                });
            },
        },
        {
            heading: (
                <Trans i18nKey="orderEntry.addressContact">
                    Contact information
                </Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.addressContact.description">
                    Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed
                    diam nonumy eirmod tempor invidunt ut labore et dolore magna
                    aliquyam erat, sed diam voluptua.
                </Trans>
            ),
            id: "address",
            fields: [
                ({ values, form }) => ({
                    size: "full",
                    component: "devicewidgetb2b",
                    className: "!mt-6",
                    configurationProduct: values.configurationProduct
                        ? {
                              ...values.configurationProduct,
                              serialnumber:
                                  values.configurationProduct?.serialnumber ||
                                  values.serialnumber,
                              requesttype:
                                  values.configurationProduct?.requesttype ||
                                  values.requesttype,
                              warranty:
                                  values.configurationProduct?.warranty ||
                                  values.warranty,
                          }
                        : null,
                    products: values.products,
                    onDelete: (product) => {
                        form.change(
                            "products",
                            values.products.filter((p) => p.id !== product.id)
                        );
                    },
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            !!values?.configurationProduct ||
                            values?.products?.length > 0
                        );
                    },
                }),
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3>
                                <Trans i18nKey={"orderEntry.addressContact"}>
                                    Contact information
                                </Trans>
                            </h3>
                        </>
                    ),
                },
                ...addressConfigB2b,
                {
                    name: "terms_of_service",
                    label: (
                        <Trans i18nKey="orderEntry.termsandconditions.label">
                            I have read and accept the
                            <a
                                href={route("termsandconditions")}
                                target={"_blank"}
                            >
                                Terms & Conditions
                            </a>
                            and the
                            <a href={route("privacy")} target={"_blank"}>
                                Privacy Statement
                            </a>
                        </Trans>
                    ),
                    size: "full",
                    type: "checkbox",
                    required: true,
                },
                {
                    name: "packaging_instruction",
                    label: (
                        <Trans i18nKey="orderEntry.packaging_instruction.label">
                            I have noticed the{" "}
                            <a
                                href="https://support.bissell.com/app/answers/detail/a_id/7280/~/packing-instructions-%26-box-selection#:~:text=Position%20the%20heaviest%20part%20of,box%20securely%20with%20packing%20tape."
                                target={"_blank"}
                            >
                                Packing Instructions
                            </a>
                        </Trans>
                    ),
                    size: "full",
                    type: "checkbox",
                    required: true,
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                return [
                    WorkflowQuestionsSchema.safeParse(values).success,
                ].every((isValid) => isValid);
            },
        },
    ],
    actions: [
        {
            fields: [
                {
                    text: <Trans i18nKey="orderEntry.send">Send</Trans>,
                    size: "half",
                    component: "button",
                    action: "next",
                    color: "secondary",
                    conditionFunction: (values, orderEntryState) => {
                        const validAddress =
                            values.name &&
                            values.city &&
                            values.street &&
                            values.zip &&
                            values.country_id &&
                            validatePhoneNumber.test(values.phone) &&
                            validateEmailRegEx.test(values.email);

                        return values.terms_of_service && values.packaging_instruction && validAddress;
                    },
                },
            ],
        },
    ],
    isMultiOrder: true,
    success: function ({ response }) {
        const orders = response
            ?.filter((item) => item.success)
            ?.map((item) => item.order);
        const notSuccessfull = response?.filter((item) => !item.success);

        return (
            <div>
                <h2 style={{ marginTop: 50 }} className={"text-center"}>
                    {notSuccessfull.length === 0 ? (
                        <Trans i18nKey="orderEntry.ordersSuccessfullyCreated">
                            Orders successfully created
                        </Trans>
                    ) : (
                        <Trans i18nKey="orderEntry.orderCreatedWithErrors">
                            Orders created with errors
                        </Trans>
                    )}
                </h2>

                <div className="mt-8 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle">
                            <table className="min-w-full">
                                <thead>
                                    <tr>
                                        <th
                                            scope="col"
                                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 lg:pl-8"
                                        >
                                            <Trans i18nKey="ordernumber">
                                                Order number
                                            </Trans>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            <Trans i18nKey="orderEntry.requestType">
                                                Request type
                                            </Trans>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            <Trans i18nKey="orderEntry.serialNumber.serialNumber">
                                                Serial number
                                            </Trans>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            <Trans i18nKey="queueDetail.product">
                                                Product
                                            </Trans>
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            <Trans i18nKey="orderEntry.serialDynamic.warranty">
                                                Warranty
                                            </Trans>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className=" bg-white">
                                    {notSuccessfull.map((order) => {
                                        return (
                                            <tr
                                                key={order.product.serialnumber}
                                                className={
                                                    "border border-t-2 border-t-black border-solid"
                                                }
                                            >
                                                <td className="py-4 pl-4 pr-3 text-sm font-medium sm:pl-6 lg:pl-8 text-red-500 ">
                                                    <div
                                                        className={
                                                            "flex  items-center "
                                                        }
                                                    >
                                                        <WarningIcon
                                                            className={
                                                                "w-5 h-5 text-red-500 ml-1"
                                                            }
                                                        />
                                                        {
                                                            order.product
                                                                .serialnumber
                                                        }
                                                    </div>
                                                    <p className={"block"}>
                                                        <Trans
                                                            i18nKey={
                                                                "orderEntry.multipleOrders.generalError"
                                                            }
                                                        >
                                                            Error while creating
                                                            order. Order data
                                                            was not saved.
                                                            Please try again.
                                                        </Trans>
                                                    </p>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {order.product.requesttype}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"></td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"></td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"></td>
                                            </tr>
                                        );
                                    })}
                                    {orders.map((order) => {
                                        return (
                                            <tr
                                                key={order.number}
                                                className={
                                                    "border border-t-2 border-t-black border-solid"
                                                }
                                            >
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8">
                                                    <a
                                                        href={route(
                                                            "track.index",
                                                            {
                                                                serialnumber:
                                                                    order?.number,
                                                            }
                                                        )}
                                                        className={
                                                            "underline text-secondary"
                                                        }
                                                        target={"_blank"}
                                                    >
                                                        {order.number}
                                                    </a>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {order?.requesttype?.name}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {
                                                        order?.orderdetails?.[0]
                                                            ?.serialnumber
                                                    }
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {
                                                        order?.orderdetails?.[0]
                                                            ?.product
                                                            ?.description
                                                    }
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {order?.orderdetails?.[0]
                                                        ?.warranty ===
                                                    "in_warranty" ? (
                                                        <Trans i18nKey="orderEntry.serialDynamic.warranty">
                                                            Warranty
                                                        </Trans>
                                                    ) : (
                                                        <Trans i18nKey="orderEntry.serialDynamic.noWarranty">
                                                            No warranty
                                                        </Trans>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/*<SuccessWrapper className={'bg-blue-100'}>*/}
                {/*    <p>*/}
                {/*        Your reference number is:*/}
                {/*    </p>*/}
                {/*    <p>*/}
                {/*        <b>{ response?.order?.batch_reference }</b>*/}
                {/*    </p>*/}
                {/*</SuccessWrapper>*/}
                {/*<div className={'text-right'}>*/}
                {/*    <MuiButton*/}
                {/*        style={{ marginTop: 50}}*/}
                {/*        variant="contained"*/}
                {/*        color={"secondary"}*/}
                {/*        href={'/'}*/}
                {/*        component={InertiaLink}*/}
                {/*    >*/}
                {/*        <Trans i18nKey="common.home">*/}
                {/*            Home*/}
                {/*        </Trans>*/}
                {/*    </MuiButton>*/}
                {/*</div>*/}
            </div>
        );
    },
};
